import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  BookOpen, ArrowLeft, Clock, Bookmark, BookmarkCheck, X, ChevronRight,
  Apple, Heart, Baby, Users, Activity, Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EducationHub = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [savedArticles, setSavedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showSaved, setShowSaved] = useState(false);

  const categories = [
    { id: 'all', name: 'All Topics', icon: BookOpen },
    { id: 'nutrition', name: 'Nutrition', icon: Apple },
    { id: 'preventive_health', name: 'Preventive Health', icon: Heart },
    { id: 'womens_health', name: "Women's Health", icon: Users },
    { id: 'child_health', name: 'Child Health', icon: Baby },
    { id: 'lifestyle', name: 'Lifestyle Habits', icon: Activity },
    { id: 'immunisation', name: 'Immunisation', icon: Shield }
  ];

  useEffect(() => {
    fetchArticles();
    if (user) {
      fetchSavedArticles();
    }
  }, [activeCategory, user]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const category = activeCategory === 'all' ? '' : activeCategory;
      const { data } = await axios.get(`${API}/education/articles${category ? `?category=${category}` : ''}`);
      setArticles(data.articles || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedArticles = async () => {
    try {
      const { data } = await axios.get(`${API}/education/saved`, {
        withCredentials: true
      });
      setSavedArticles(data.articles || []);
    } catch (error) {
      console.error('Error fetching saved articles:', error);
    }
  };

  const handleSaveArticle = async (articleId) => {
    if (!user) {
      toast.error('Please log in to save articles');
      return;
    }

    try {
      const isSaved = savedArticles.some(a => a.id === articleId);
      
      if (isSaved) {
        await axios.delete(`${API}/education/saved/${articleId}`, {
          withCredentials: true
        });
        setSavedArticles(savedArticles.filter(a => a.id !== articleId));
        toast.success('Article removed from saved');
      } else {
        await axios.post(`${API}/education/saved?article_id=${articleId}`, {}, {
          withCredentials: true
        });
        await fetchSavedArticles();
        toast.success('Article saved!');
      }
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error('Failed to save article');
    }
  };

  const isArticleSaved = (articleId) => {
    return savedArticles.some(a => a.id === articleId);
  };

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.id === category);
    return cat ? cat.icon : BookOpen;
  };

  const getCategoryColor = (category) => {
    const colors = {
      nutrition: 'bg-green-50 text-green-700 border-green-200',
      preventive_health: 'bg-red-50 text-red-700 border-red-200',
      womens_health: 'bg-purple-50 text-purple-700 border-purple-200',
      child_health: 'bg-blue-50 text-blue-700 border-blue-200',
      lifestyle: 'bg-amber-50 text-amber-700 border-amber-200',
      immunisation: 'bg-teal-50 text-teal-700 border-teal-200'
    };
    return colors[category] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const displayArticles = showSaved ? savedArticles : articles;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 md:top-16 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Health Education Hub</h1>
                <p className="text-sm text-gray-600">Evidence-based knowledge to prevent health issues</p>
              </div>
            </div>
            {user && (
              <Button
                variant={showSaved ? "default" : "outline"}
                size="sm"
                onClick={() => setShowSaved(!showSaved)}
                className="flex items-center gap-2"
              >
                <Bookmark className="h-4 w-4" />
                <span className="hidden sm:inline">Saved</span>
                {savedArticles.length > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {savedArticles.length}
                  </Badge>
                )}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Category Tabs */}
        {!showSaved && (
          <div className="mb-6 overflow-x-auto pb-2">
            <div className="flex gap-2 min-w-max">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      activeCategory === category.id
                        ? 'bg-healthloop-mint-600 text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium whitespace-nowrap">{category.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Articles Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : displayArticles.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                {showSaved ? 'No saved articles yet' : 'No articles found'}
              </p>
              {showSaved && (
                <Button
                  variant="ghost"
                  onClick={() => setShowSaved(false)}
                  className="mt-4"
                >
                  Browse Articles
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayArticles.map((article) => {
              const CategoryIcon = getCategoryIcon(article.category);
              
              return (
                <Card 
                  key={article.id}
                  className="hover:shadow-lg transition-all duration-200 cursor-pointer group"
                >
                  <CardHeader onClick={() => setSelectedArticle(article)}>
                    <div className="flex items-start justify-between mb-2">
                      <Badge className={`${getCategoryColor(article.category)} border`}>
                        <CategoryIcon className="h-3 w-3 mr-1" />
                        {article.category.replace(/_/g, ' ')}
                      </Badge>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveArticle(article.id);
                        }}
                        className="text-gray-400 hover:text-healthloop-mint-600 transition-colors"
                      >
                        {isArticleSaved(article.id) ? (
                          <BookmarkCheck className="h-5 w-5 text-healthloop-mint-600" />
                        ) : (
                          <Bookmark className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    <CardTitle className="text-base sm:text-lg group-hover:text-healthloop-mint-600 transition-colors">
                      {article.title}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm line-clamp-2">
                      {article.excerpt}
                    </CardDescription>
                  </CardHeader>
                  <CardContent onClick={() => setSelectedArticle(article)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Clock className="h-3 w-3" />
                        <span>{article.reading_time} min read</span>
                      </div>
                      <Button variant="ghost" size="sm" className="text-xs">
                        Read More
                        <ChevronRight className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Article Detail Modal */}
      {selectedArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 md:top-16 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">{selectedArticle.title}</h2>
              <button
                onClick={() => setSelectedArticle(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Article Meta */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <Badge className={`${getCategoryColor(selectedArticle.category)} border`}>
                  {selectedArticle.category.replace(/_/g, ' ')}
                </Badge>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>{selectedArticle.reading_time} min read</span>
                </div>
                <button
                  onClick={() => handleSaveArticle(selectedArticle.id)}
                  className="flex items-center gap-2 text-sm text-healthloop-mint-600 hover:text-healthloop-mint-700"
                >
                  {isArticleSaved(selectedArticle.id) ? (
                    <>
                      <BookmarkCheck className="h-4 w-4" />
                      <span>Saved</span>
                    </>
                  ) : (
                    <>
                      <Bookmark className="h-4 w-4" />
                      <span>Save for Later</span>
                    </>
                  )}
                </button>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedArticle.tags.map((tag, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>

              {/* Content */}
              <div className="prose prose-sm max-w-none mb-6">
                {selectedArticle.content.split('\n').map((paragraph, idx) => {
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    return (
                      <h3 key={idx} className="font-bold text-gray-900 mt-4 mb-2">
                        {paragraph.replace(/\*\*/g, '')}
                      </h3>
                    );
                  }
                  if (paragraph.trim() === '') return null;
                  return (
                    <p key={idx} className="text-gray-700 mb-3 leading-relaxed">
                      {paragraph}
                    </p>
                  );
                })}
              </div>

              {/* Key Takeaways */}
              <div className="bg-healthloop-mint-50 border border-healthloop-mint-200 rounded-lg p-4">
                <h3 className="font-bold text-gray-900 mb-3">Key Takeaways</h3>
                <ul className="space-y-2">
                  {selectedArticle.key_takeaways.map((takeaway, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-healthloop-mint-600 mt-0.5">✓</span>
                      <span>{takeaway}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Author */}
              <div className="mt-6 pt-6 border-t border-gray-200 text-sm text-gray-600">
                <p>By {selectedArticle.author}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EducationHub;
